import React, { useState } from "react";
import { toast } from "sonner";
import { api, fmtMoney, formatApiError } from "../lib/api";

const SETTLEMENT_OPTIONS = [
  { value: "stripe", label: "Stripe card / hosted checkout" },
  { value: "swift", label: "SWIFT bank transfer" },
  { value: "usdt", label: "USDT (stablecoin)" },
  { value: "other", label: "Other (please specify in notes)" },
];

export default function QuoteRequestDialog({ open, onClose, projectId, projectName, tons, currency, totalUsd, unitPriceUsd }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [f, setF] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    intended_use: "",
    settlement_method: "swift",
    target_close_date: "",
    notes: "",
  });

  if (!open) return null;

  const update = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/quotes/request", {
        project_id: projectId,
        tons: Number(tons),
        currency,
        ...f,
      });
      setDone(data);
      toast.success(`Quote request ${data.ref} submitted. Our team will reply within 1 business day.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-surface border border-line w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="quote-dialog"
      >
        <div className="border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-tag">Quote request</div>
            <div className="font-serif text-xl mt-1">{projectName || "UCS purchase"}</div>
            <div className="text-xs font-mono text-ink2 mt-1">
              {Number(tons).toFixed(0)} tCO₂ · @ ${unitPriceUsd?.toFixed(2)}/t · ~{fmtMoney(totalUsd, "USD")} USD
            </div>
          </div>
          <button onClick={onClose} className="text-ink2 hover:text-ink text-2xl leading-none" data-testid="quote-close" aria-label="Close">×</button>
        </div>

        {done ? (
          <div className="p-8 space-y-4 text-center">
            <div className="mx-auto w-14 h-14 bg-leaf text-white flex items-center justify-center font-serif text-2xl">✓</div>
            <div className="font-serif text-2xl">Quote request received.</div>
            <div className="label-tag">Reference</div>
            <div className="mono-num text-2xl" data-testid="quote-ref">{done.ref}</div>
            <p className="text-sm text-ink2 max-w-md mx-auto">
              Our compliance and trading desk will reply within 1 business day to the email you provided. Keep this reference handy.
            </p>
            <button onClick={onClose} className="btn-primary !px-8 !py-3 !text-xs" data-testid="quote-done-close">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-5" data-testid="quote-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Company name" required>
                <input data-testid="q-company" required value={f.company_name} onChange={(e) => update("company_name", e.target.value)} className="input-field" />
              </Field>
              <Field label="Contact name" required>
                <input data-testid="q-contact-name" required value={f.contact_name} onChange={(e) => update("contact_name", e.target.value)} className="input-field" />
              </Field>
              <Field label="Email" required>
                <input data-testid="q-email" type="email" required value={f.contact_email} onChange={(e) => update("contact_email", e.target.value)} className="input-field" />
              </Field>
              <Field label="Phone (incl. country code)" required>
                <input data-testid="q-phone" required value={f.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} placeholder="+1 555 555 1234" className="input-field" />
              </Field>
            </div>
            <Field label="Intended use" required>
              <textarea data-testid="q-use" required rows={3} value={f.intended_use} onChange={(e) => update("intended_use", e.target.value)}
                placeholder="e.g. Scope 3 emissions offsetting for FY2026; ESG report disclosure; voluntary retirement against operating footprint."
                className="input-field" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Preferred settlement" required>
                <select data-testid="q-settlement" value={f.settlement_method} onChange={(e) => update("settlement_method", e.target.value)} className="input-field">
                  {SETTLEMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Target close date">
                <input data-testid="q-close-date" type="date" value={f.target_close_date} onChange={(e) => update("target_close_date", e.target.value)} className="input-field" />
              </Field>
            </div>
            <Field label="Notes (optional)">
              <textarea data-testid="q-notes" rows={2} value={f.notes} onChange={(e) => update("notes", e.target.value)} className="input-field" />
            </Field>
            <div className="text-xs text-ink2 font-mono border border-line bg-muted/30 p-3">
              Your request is reviewed by GreenBelt&apos;s compliance and trading desk. Quote validity, FX, and settlement timing are confirmed in writing within 1 business day.
            </div>
            <div className="flex items-center justify-between gap-3 pt-2">
              <button type="button" onClick={onClose} className="text-xs font-mono uppercase tracking-wider text-ink2 hover:text-ink underline underline-offset-4" data-testid="q-cancel">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary !px-6 !py-3 !text-xs disabled:opacity-50" data-testid="q-submit">
                {busy ? "Submitting…" : "Submit quote request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-tag">{label}{required && <span className="text-danger ml-1">*</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
